import { branch, buildSteps, split, stepTemplate } from "@airhelp/funnel";
import { FlightDetailsStep } from "../steps/FlightDetails";
import { AnyConnectionsStep } from "../steps/AnyConnections";
import { FlightSelectorStep } from "../steps/FlightSelector";
import { ItineraryDetailsStep } from "../steps/ItineraryDetails";
import { ClaimTypeStep } from "../steps/ClaimType";
import { TotalDelayStep } from "../steps/TotalDelay";
import { InformedBeforehandStep } from "../steps/InformedBeforehand";
import { MultilegDisruptedFlightStep } from "../steps/MultilegDisruptedFlight";
import { ConnectionsStep } from "../steps/Connections";
import {
  getPreEligibilityDataSnapshot,
  PreEligibilityDataSnapshot,
} from "../state/preEligibilityState";
import { VolunteerStep } from "../steps/VolunteerStep";
import { ReactRouterFunnelDefinition } from "@airhelp/funnel/react-router";
import { DisruptedFlightDetectedStep } from "../steps/DisruptedFlightDetected";
import { getShouldCutInformedBeforehandStep } from "../state/appState";

const multilegDisruptedFlightStep = stepTemplate<PreEligibilityDataSnapshot>({
  name: "multileg-disrupted-flight",
  uri: "mobile/multileg-disrupted-flight",
  Component: MultilegDisruptedFlightStep,
});

const flightDetailsStep = stepTemplate<PreEligibilityDataSnapshot>({
  name: "flight-details",
  uri: "mobile/flight-details",
  Component: FlightDetailsStep,
});

const anyConnectionsStep = stepTemplate<PreEligibilityDataSnapshot>({
  name: "any-connections-flights",
  uri: "mobile/any-connections-flights",
  Component: AnyConnectionsStep,
});

const connectionsStep = stepTemplate<PreEligibilityDataSnapshot>({
  name: "connections",
  uri: "mobile/connections",
  Component: ConnectionsStep,
});

const flightSelectorStep = stepTemplate<PreEligibilityDataSnapshot>({
  name: "flight-selector-date",
  uri: "mobile/flight-selector-date",
  Component: FlightSelectorStep,
});

const itineraryDetailsStep = stepTemplate<PreEligibilityDataSnapshot>({
  name: "itinerary-details",
  uri: "mobile/itinerary-details",
  Component: ItineraryDetailsStep,
});

const claimTypeStep = (suffix: string) =>
  stepTemplate<PreEligibilityDataSnapshot>({
    name: `claim-type-${suffix}`,
    uri: `mobile/claim-type?flow=${suffix}`,
    Component: ClaimTypeStep,
  });

const totalDelayStep = (suffix: string) =>
  stepTemplate<PreEligibilityDataSnapshot>({
    name: `total-delay-${suffix}`,
    uri: `mobile/total-delay?flow=${suffix}`,
    Component: TotalDelayStep,
  });

const informedBeforehandStep = (suffix: string) =>
  stepTemplate<PreEligibilityDataSnapshot>({
    name: `informed-beforehand-${suffix}`,
    uri: `mobile/informed-beforehand?flow=${suffix}`,
    Component: InformedBeforehandStep,
  });

const volunteerStep = (suffix: string) =>
  stepTemplate<PreEligibilityDataSnapshot>({
    name: `volunteer-${suffix}`,
    uri: `mobile/volunteer?flow=${suffix}`,
    Component: VolunteerStep,
  });

const disruptedFlightDetectedStep = stepTemplate<PreEligibilityDataSnapshot>({
  name: "disrupted-flight-detected",
  uri: "mobile/disrupted-flight-detected",
  Component: DisruptedFlightDetectedStep,
});

const defaultDelayedFlow = (suffix: string) =>
  branch(
    [
      totalDelayStep(`default-${suffix}`)({
        meta: {
          claimShouldBeSubmitted: true,
        },
      }),
    ],
    ({ state }) => state.claimDetails.potentialRegulation !== "brazil",
  );

const brazilDelayedFlow = (suffix: string) =>
  branch(
    [
      totalDelayStep(`brazil-${suffix}`)(),
      informedBeforehandStep(`brazil-${suffix}`)({
        meta: {
          claimShouldBeSubmitted: true,
        },
      }),
    ],
    ({ state }) => state.claimDetails.potentialRegulation === "brazil",
  );

const delayedFlow = (suffix: string) =>
  branch(
    [split([defaultDelayedFlow(`delayed-${suffix}`), brazilDelayedFlow(`delayed-${suffix}`)])],
    ({ state }) => state.claimDetails.claimType === "delayed",
  );

const defaultCancelledFlow = (suffix: string) =>
  branch(
    [
      totalDelayStep(`default-${suffix}`)(),
      informedBeforehandStep(`default-${suffix}`)({
        meta: {
          claimShouldBeSubmitted: true,
        },
      }),
    ],
    ({ state }) => !state.flightDetails.isFutureFlight,
  );

const futureFlightCancelledFlow = (suffix: string) =>
  branch(
    [
      informedBeforehandStep(`future-${suffix}`)({
        meta: {
          claimShouldBeSubmitted: true,
        },
      }),
    ],
    ({ state }) => state.flightDetails.isFutureFlight,
  );

const cancelledFlow = (suffix: string) =>
  branch(
    split([
      defaultCancelledFlow(`cancelled-${suffix}`),
      futureFlightCancelledFlow(`cancelled-${suffix}`),
    ]),
    ({ state }) => state.claimDetails.claimType === "cancelled",
  );

const defaultOverbookedFlow = (suffix: string) =>
  branch(
    [
      totalDelayStep(`default-${suffix}`)(),
      volunteerStep(`default-${suffix}`)({
        meta: {
          claimShouldBeSubmitted: true,
        },
      }),
    ],
    ({ state }) => !state.flightDetails.isFutureFlight,
  );

const futureFlightOverbookedFlow = (suffix: string) =>
  branch(
    [
      volunteerStep(`future-${suffix}`)({
        meta: {
          claimShouldBeSubmitted: true,
        },
      }),
    ],
    ({ state }) => state.flightDetails.isFutureFlight,
  );

const overbookedFlow = (suffix: string) =>
  branch(
    split([
      defaultOverbookedFlow(`overbooked-${suffix}`),
      futureFlightOverbookedFlow(`overbooked-${suffix}`),
    ]),
    ({ state }) => state.claimDetails.claimType === "overbooked",
  );

const claimTypeFlows = (suffix: string) =>
  split([delayedFlow(suffix), cancelledFlow(suffix), overbookedFlow(suffix)]);

const singleLegFlow = () =>
  branch(
    [
      flightSelectorStep(),
      disruptedFlightDetectedStep({
        enabled: ({ state }) =>
          state.claimDetails.potentialClaimType === "delayed" ||
          state.claimDetails.potentialClaimType === "cancelled",
      }),
      informedBeforehandStep("single-leg")({
        meta: {
          claimShouldBeSubmitted: true,
        },
        enabled: ({ state }) => {
          return !getShouldCutInformedBeforehandStep || state.claimDetails.prefilled;
        },
      }),
      claimTypeStep("single-leg")(),
      claimTypeFlows("single-leg"),
    ],
    ({ state }) => !state.flightDetails.anyConnections,
  );

const multilegFlow = () =>
  branch(
    [
      connectionsStep(),
      multilegDisruptedFlightStep(),
      itineraryDetailsStep(),
      claimTypeStep("multi-leg")(),
      claimTypeFlows("multi-leg"),
    ],
    ({ state }) => state.flightDetails.anyConnections,
  );

export const preEligibilityMobileFunnelDefinition: ReactRouterFunnelDefinition<PreEligibilityDataSnapshot> =
  {
    basePath: `/claims/new/`,
    steps: buildSteps([
      flightDetailsStep(),
      anyConnectionsStep(),
      split([singleLegFlow(), multilegFlow()]),
    ]),
    getState: () => {
      return getPreEligibilityDataSnapshot();
    },
  };
